// Seeding a NEW project from a page that already knows the room and/or the
// gear — the SEO funnel behind `/gear/*` pairing pages, layout-fit pages and
// room-size pages (docs/plans/2026-09-17-seo-beat-competitors.md S12; the
// gear half is designed in the SEO architecture scratchpad §3.7/§4.5).
//
// `EditorCta.tsx` (`web/src/components/seo/EditorCta.tsx`, `editorHref`)
// already builds the outgoing link: `/room/new?w=&l=&h=&units=ft|m&use=` for
// a room, `&sys=<encodeSelection(selection)>` for a system, either or both.
// This module is the read side: parsing that query back into a validated
// seed, and turning the seed into an `EditorDoc` for a fresh project.
//
// Lives in `@roomtreatment/av-compat`, not `@roomtreatment/room-flow` (S12's
// own suggested location for the room-only half): `av-compat` already
// depends on `@roomtreatment/room-flow` as a devDependency (its golden-build
// tests use `generateSpeakerLayout`), so a build-graph edge the other way
// (`room-flow` depending on `av-compat`, whose own `decodeSelection` +
// `buildSystemDoc` the gear half needs) would be circular — the same
// directionality `packages/core/src/__tests__/editorReducerContract.test.ts`
// documents for `core`/`room-flow`. `av-compat` already depends on `core`,
// which is all the room half needs, so the combined `{room?, system?}`
// parser and the doc it builds both live in one place instead of splitting
// a single query string's parsing across two packages.
//
// Every parse step is strict-but-forgiving: a malformed or out-of-range
// value drops just that piece (room, or system, or one field of one) rather
// than failing the whole parse — a gear page's link should still open SOME
// useful project even if one param got mangled in transit.

import {
  createBlankDoc, ftToM, normalizeEditorDoc,
  type EditorDoc, type RoomDims, type Vec3,
} from '@roomtreatment/core';
import type { Product } from '@roomtreatment/av-catalog';
import { buildSystemDoc, decodeSelection, type SystemSelection } from './system';
import { displayRole, mediaBinding, startingPicture, type PlaceDisplay } from './media';

// ── Room seed ───────────────────────────────────────────────────────────

/** No hard limit exists elsewhere in the app (the floorplan is drawn
 *  freehand); these are a sanity bound against garbage input, sized well
 *  outside any real room — generous enough for a great room, small enough
 *  that a stray extra digit or a unit mix-up (metres typed as feet) can't
 *  produce a document nothing can render sensibly. */
export const MIN_ROOM_DIM_M = 1;
export const MAX_ROOM_DIM_M = 20;
export const MIN_CEILING_M = 1.5;
export const MAX_CEILING_M = 8;

/** `use` has no `EditorDoc` field to land in (there is no onboarding "goal"
 *  question in the Studio) — it survives parsing only for analytics, so
 *  validation is a safety/size bound, not a fixed enum. `EditorCta.tsx`'s own
 *  doc comment already gives `studio` as an example alongside the plan's
 *  `theater`/`music-studio`/`recording-studio`/`listening-space`, and its
 *  existing test (`seoKitJsonLd.test.ts`) exercises `use: 'studio'` — a
 *  strict 4-value enum would silently drop an already-shipped value. */
const USE_RE = /^[a-z0-9-]{1,40}$/;

export interface RoomSeed {
  /** Width. In `units`, not yet converted — see `seedToDims`. */
  w: number;
  /** Length (front wall to back wall). */
  l: number;
  /** Ceiling height. */
  h: number;
  units: 'ft' | 'm';
  /** What the room is for, e.g. `theater` — free text, bounded. See above. */
  use?: string;
}

function parseUnits(raw: string | null): RoomSeed['units'] | null {
  return raw === 'ft' || raw === 'm' ? raw : null;
}

/** A positive decimal only — no sign, exponent, `Infinity`/`NaN` spellings,
 *  or trailing garbage — within `[minM, maxM]` once converted to metres, so
 *  the bound is physical rather than unit-dependent. Returns the value AS
 *  GIVEN (still in `units`); `seedToDims` does the conversion. */
function parseDim(raw: string | null, units: RoomSeed['units'], minM: number, maxM: number): number | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  // A bound on the string itself, before it ever reaches a regex or Number():
  // no real dimension needs more than a few characters.
  if (trimmed.length === 0 || trimmed.length > 20 || !/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  const meters = units === 'ft' ? ftToM(n) : n;
  return meters >= minM && meters <= maxM ? n : null;
}

function parseUse(raw: string | null): string | undefined {
  if (raw == null) return undefined;
  const trimmed = raw.trim().toLowerCase();
  return USE_RE.test(trimmed) ? trimmed : undefined;
}

/**
 * `w`, `l`, `h` and `units` are all-or-nothing: a room seed with one missing
 * or out-of-range dimension is not a room, so the whole thing drops rather
 * than opening a partially-seeded, oddly-shaped room. `use` is independent
 * and optional.
 */
export function parseRoomSeed(params: URLSearchParams): RoomSeed | null {
  const units = parseUnits(params.get('units'));
  if (!units) return null;
  const w = parseDim(params.get('w'), units, MIN_ROOM_DIM_M, MAX_ROOM_DIM_M);
  const l = parseDim(params.get('l'), units, MIN_ROOM_DIM_M, MAX_ROOM_DIM_M);
  const h = parseDim(params.get('h'), units, MIN_CEILING_M, MAX_CEILING_M);
  if (w == null || l == null || h == null) return null;
  const use = parseUse(params.get('use'));
  return use ? { w, l, h, units, use } : { w, l, h, units };
}

/** Width -> Lx, length -> Ly (the front wall is at y = 0 in the plan, per
 *  `EditorCta.tsx`'s own comment), converted to the document's canonical
 *  metres. */
export function seedToDims(seed: RoomSeed): RoomDims {
  const toM = (v: number) => (seed.units === 'ft' ? ftToM(v) : v);
  return { Lx: toM(seed.w), Ly: toM(seed.l), H: toM(seed.h) };
}

// ── System (gear) seed ──────────────────────────────────────────────────

/** `decodeSelection` always returns a `SystemSelection` — even `sys=` with
 *  nothing recognisable in it decodes to `{layout: 'stereo'}`. That is not a
 *  system a page asked to seed; this is the line between "a system was
 *  named" and "nothing survived decoding". */
export function hasSystemSelection(selection: SystemSelection): boolean {
  return !!(
    selection.avr || selection.fronts || selection.center || selection.surrounds
    || selection.heights || selection.sub || (selection.amps && selection.amps.length > 0)
  );
}

/** Far more than any real selection needs (the checker's own worked example,
 *  five roles plus a layout, is under 100 characters) — a defensive cap so a
 *  pathologically long `sys` value is dropped before `decodeSelection` ever
 *  walks it. */
const MAX_SYS_LEN = 2000;

export function parseSystemSeed(params: URLSearchParams, catalog: ReadonlyMap<string, Product>): SystemSelection | undefined {
  const raw = params.get('sys');
  if (!raw || raw.length > MAX_SYS_LEN) return undefined;
  const selection = decodeSelection(raw, catalog);
  return hasSystemSelection(selection) ? selection : undefined;
}

// ── Display seed ────────────────────────────────────────────────────────

/** `media=<id>`: the TV, projector or screen model a new project opens with
 *  as the room's display — a gear page's "Use in my room" on a display, which
 *  has no role in a `sys` selection (that is the compatibility checker's
 *  contract, speakers and electronics only). */
export const DISPLAY_SEED_PARAM = 'media';

export function parseDisplaySeed(params: URLSearchParams, catalog: ReadonlyMap<string, Product>): string | undefined {
  const id = params.get(DISPLAY_SEED_PARAM);
  const product = id ? catalog.get(id) : undefined;
  return product && displayRole(product) ? product.id : undefined;
}

// ── Combined seed ───────────────────────────────────────────────────────

export interface ParsedSeed {
  room?: RoomSeed;
  system?: SystemSelection;
  /** A display model's id (`media=`). */
  display?: string;
}

/** Parses every part independently: an invalid or absent room never blocks
 *  a valid system, and vice versa (S12 + the gear-matching design both seed
 *  a bare room, a bare system, or both on one link); a display rides along
 *  with either or stands alone. */
export function parseEditorSeed(params: URLSearchParams, catalog: ReadonlyMap<string, Product>): ParsedSeed {
  const room = parseRoomSeed(params) ?? undefined;
  const system = parseSystemSeed(params, catalog);
  const display = parseDisplaySeed(params, catalog);
  return room || system || display ? { room, system, display } : {};
}

/** What a mounted editor needs to know a seed happened, without re-deriving
 *  it from the document: whether to switch display units, what to log, and
 *  (`use`) a value with nowhere else to live. `null` means nothing was
 *  seeded — a caller should behave exactly as it does today. */
export interface SeededInfo {
  units?: RoomSeed['units'];
  use?: string;
  hasSystem: boolean;
}

export function seededInfoFor(seed: ParsedSeed): SeededInfo | null {
  if (!seed.room && !seed.system && !seed.display) return null;
  // A display is gear too: for `seeded_from`, a link that named one named a system.
  return { units: seed.room?.units, use: seed.room?.use, hasSystem: !!seed.system || !!seed.display };
}

// ── Building the document ───────────────────────────────────────────────

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

/**
 * `buildSystemDoc` places every speaker at an ITU/Dolby-correct angle and
 * the standard 2 m seat distance — but inside ITS OWN synthetic 5x6x2.8 m
 * room. Dropping a seeded room's real dimensions in without moving anything
 * would let a smaller room, or a lower ceiling than 2.8 m, place a speaker
 * outside a wall or a height channel above the ceiling.
 *
 * This is a rigid re-anchor, not a second placement algorithm: every angle
 * and distance `buildSystemDoc` computed survives exactly.
 *   - X re-centres on the new room's width — X has no fixed reference, the
 *     synthetic room only centred it on ITS width.
 *   - Z is left alone: z = 0 is the front wall in every room (system.ts's
 *     own "Forward direction" convention), so front-anchored positions
 *     already line up without moving.
 *   - X, Y and Z are then clamped inside the new walls/ceiling with a small
 *     margin, so an unusually small room or low ceiling degrades to "snug
 *     against the wall" instead of "outside it" — still one drag away from
 *     wherever the owner actually wants it, since design is free (ADR 014).
 */
function fitToRoom(doc: EditorDoc, dims: RoomDims): EditorDoc {
  const margin = 0.15;
  const dx = dims.Lx / 2 - doc.dims.Lx / 2;
  const fit = ([x, y, z]: Vec3): Vec3 => [
    clamp(x + dx, margin, Math.max(margin, dims.Lx - margin)),
    clamp(y, 0, Math.max(0, dims.H - margin)),
    clamp(z, margin, Math.max(margin, dims.Ly - margin)),
  ];
  return {
    ...doc,
    dims,
    speakers: doc.speakers.map((s) => ({ ...s, position: fit(s.position) })),
    listener: doc.listener ? { ...doc.listener, position: fit(doc.listener.position) } : doc.listener,
    gear: (doc.gear ?? []).map((g) => ({ ...g, position: fit(g.position) })),
  };
}

/**
 * The new project's initial document: a system's speakers and gear (bound
 * `productId`s, so the Studio's System view and compatibility checklist see
 * them at once) re-anchored into a seeded room's real dimensions, or a bare
 * seeded room, or `null` when there is nothing to seed (the caller should
 * fall back to its own blank-room default). Always `editorMode: 'advanced'`
 * — the Studio, like `RoomEditor.tsx`'s own `blankAdvancedDoc()` — so the
 * mode survives the first save.
 *
 * A display seed becomes the room's display, placed by `place` (the editor's
 * panel-surface defaults, which this package does not depend on); without
 * `place` it is left out.
 */
export function buildSeededDoc(
  seed: ParsedSeed,
  catalog: ReadonlyMap<string, Product>,
  place?: PlaceDisplay,
): EditorDoc | null {
  if (!seed.room && !seed.system && !seed.display) return null;
  const systemDoc = seed.system ? buildSystemDoc(seed.system, catalog) : null;
  const dims = seed.room ? seedToDims(seed.room) : (systemDoc?.dims ?? createBlankDoc().dims);
  const base = systemDoc ? fitToRoom(systemDoc, dims) : { ...createBlankDoc(), dims };
  const doc = normalizeEditorDoc({ ...base, editorMode: 'advanced' });
  const display = seed.display ? catalog.get(seed.display) : undefined;
  return display && place ? withDisplay(doc, display, place) : doc;
}

/** `doc` with `product` as its display, where a display belongs. */
function withDisplay(doc: EditorDoc, product: Product, place: PlaceDisplay): EditorDoc {
  const role = displayRole(product);
  const placed = role ? place(doc, role.kind, startingPicture(role.kind, product)) : null;
  const patch = placed ? mediaBinding(placed, product) : null;
  if (!placed || !patch) return doc;
  return { ...doc, media: [...(doc.media ?? []), { ...placed, ...patch, id: 'seeded-display' }] };
}
